import { useEffect, useState } from "react";
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { store } from 'Store';
import { Row, Col, Card } from "react-bootstrap";
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import { http } from "Utils";
import { ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { TextButton } from "Components/Button";
import { PATHS } from "Utils/PagePaths";

const TermOfUse = () => {

  const { token } = useParams();
  const loginState = store.getState().loginInfo;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  // back to login page
  const handleCancel = () => {
    navigate(PATHS.public['Sign In']);
  }

  /*
  UserActivationController.java 
  url: /api/activation/validateToken
  ValidateResetPasswordTokenRequest : {
   String resetPasswordToken
  }
   */
  const validateToken = async (token) => {

    // the activation can only be operated when the user is not login!
    if (loginState.token) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.GENERAL_ERROR.title,
          msg: "Please sign out to activate the account.",
          showContactHelp: false
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        }
      });

      return;
    }

    var url = "/api/activation/validateToken";
    var body = {
      resetPasswordToken: token
    }

    return http.post(url, body, {
      headers: {
      }
    }).then((res) => {
      //console.log("validateToken", res);
      if (res.status !== 1) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: res.globalErrorMessageList,
            showContactHelp: false
          },
          redirect: {
            redirect: true,
            redirectFirst: true,
            path: PATHS.public['Sign In'],
            data: {}
          }
        });
        return;
      }

      if (res.status === 1) {
        //navigate to setup password page
        if (res.data?.isAgreedTou === 1) {
          navigate("/activation/token/" + token + "/2");
        }

        //stay on the term of use page
        setIsLoading(false);
      }
    }).catch((error) => {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error: error
      });
    });
  }

  const agreeTermOfUse = async () => {

    var url = "/api/activation/agreeTou";
    var body = {
      resetPasswordToken: token
    }

    return http.post(url, body, {}).then((res) => {
      //console.log("agreeTermOfUse api", res);
      if (res.status !== 1) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: res.globalErrorMessageList,
            showContactHelp: false
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          }
        });
      }
      //continue to the setup password page
      if (res.status === 1) {
        navigate("/activation/token/" + token + "/2");
      }
    }).catch((error) => {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error: error
      });
    });
  }

  useEffect(() => {
    //console.log("TermOfUse use effect", token);
    validateToken(token);
  }, []);

  return (
    <>{isLoading ? null :
      <>
        <Row>
          <Col xl={10} lg={11} md={11} sm={12} className="mb-3">
            <Card className="p-4">
              <Card.Body className="text-start">
                <h1 id="mainContent" className="text-start mb-3 blue bold">PSOPPC Website Terms of Use</h1>
                <br />
                <h2 className="text-start mb-2 form-title-no-border">Agreement between User and Patient Safety Privacy Protection Center (PSOPPC)</h2>
                <p>
                  The PSOPPC website comprises of various Web pages operated by the Agency for Healthcare Research and Quality (AHRQ) and supported by the Patient Safety Organization Privacy Protection Center (PSOPPC) under contract 75Q80121C00004.
                  The PSOPPC website is offered to users under the condition of acceptance without modifications of the terms, conditions, and notices contained herein. Use of the PSOPPC website constitutes agreement to all such terms, conditions, and notices.
                </p>
                <br />
                <h2 className="text-start mb-2 form-title-no-border">Modification of these Terms of Use</h2>
                <p>
                  AHRQ reserves the right to change the terms, conditions, and notices, under which the PSOPPC website is offered.
                  The PSOPPC will contact users if and when any changes occur.
                  If there are changes to the terms, conditions and notices under which the PSOPPC website is offered, the users will be notified at login and request agreement of the updated Terms of Use.
                </p>
                <br />
                <h2 className="text-start mb-2 form-title-no-border">Privacy Act</h2>
                <p>
                  Information contained in this system is subject to the Privacy Act of 1974 (5 U.S.C. 552A, as amended).
                  Personal information contained in this system may be used only by authorized persons to conduct official business. Any individual responsible for unauthorized disclosure or misuse of personal information may be subject to monetary fines.
                </p>
                <p>Read about the <a href={PATHS.public["Privacy Policy"]}>Privacy Policy</a>.</p>
                <br />
                <h2 className="text-start mb-2 form-title-no-border">Quality of Information</h2>
                <p>
                  Every effort has been made to ensure the accuracy and completeness of the electronic documents and resources provided on the website.
                  However, AHRQ makes no warranties, expressed or implied, regarding errors or omissions and assumes no legal liability or responsibility for loss or damage resulting from the use of information contained within.
                  Improvements and/or changes in the PSOPPC website may be made at any time. Technical assistance received via the PSOPPC website should not be relied upon without consulting an appropriate professional for specific advice tailored to that specific situation.
                  All requests for correction of information on the PSOPPC website must be submitted via letter, or email to:
                </p>
                <h3 style={{ "fontSize": "16px" }} className="text-start mb-2 form-title-no-border" >PSOPPC Help Desk</h3>
                <div className="mb-4 pb-4 ms-4">
                  <br />
                  <span className="help-desk-icon"><MailOutlinedIcon></MailOutlinedIcon></span> : <a href="mailto:support@psoppc.org">support@psoppc.org</a>
                  <br /><br /><br />
                  <span className="help-desk-icon"><LocalPhoneOutlinedIcon></LocalPhoneOutlinedIcon></span> : <span style={{ "color": "#0d6efd" }}>(866) 571-7712</span> (Mon-Fri, 9:00 AM - 5:30 PM ET)
                </div>
                <h2 className="text-start mb-2 form-title-no-border">Government System Usage Policy</h2>
                <p>
                  This website is provided only for official business use and is funded by the United States Government.
                  This system is monitored to ensure proper operation, to verify the functioning of applicable security features and for other like purposes.
                  Anyone using this system expressly consents to such monitoring and is advised that if such monitoring reveals evidence of possible criminal activity, system personnel may provide the evidence of such activity to law enforcement officials.
                  Unauthorized attempts to upload or change information; to defeat or circumvent security features; or to utilize this system for other than its intended purposes are prohibited.
                  We encourage you to read the HHS Rules of Behavior for more details.
                </p>
                <br />
                <p>
                  Unauthorized access to this computer system is prohibited by law (reference the Computer Fraud and Abuse Act of 1986 and Title 18 U.S.C., Section 1001 and 1030).
                  If you are not authorized to access this system you must sign out immediately or exit.
                </p>
                <br />
                <p>
                  Federal, State, and/or local laws may provide criminal penalties for any person illegally accessing or using a Government owned or operated computer system.
                </p>
                <br />
                <h2 className="text-start mb-2 form-title-no-border">Additional Terms for Account Holders</h2>
                <p>
                  This website is funded by AHRQ for use by the workforce of AHRQ-listed PSOs.
                  The following Terms of Use are designed to inform users who have registered and received account privileges in the PSOPPC secure website.
                  The additional terms are specific to the practices regarding collection, use, and disclosure of information that the user may access as a signed-in user of the PSOPPC website.
                  The Terms of Use in their entirety apply to Account Holders.
                </p>
                <br />
                <p>
                  In order to ensure the integrity, confidentiality, availability and security of information maintained by the PSOPPC, and to permit appropriate disclosure and use of such data as permitted by law; the Account Holder agrees to comply with the following specific paragraphs.
                  The Account Holder represents and warrants further that the person will not disclose, release, reveal, show, sell, rent, lease, loan, or otherwise grant access to their User ID and password to the PSOPPC secure Web site to any individual(s).
                  The Account Holder agrees to notify the PSOPPC Help Desk should they suspect their User ID and password to be compromised.
                  In the event the user account should be terminated and a new User ID and password should be issued.
                </p>
                <br />
                <p>The Account Holder agrees to establish appropriate administrative, technical, and physical safeguards to protect the confidentiality of the information accessed through the PSOPPC secure website and to prevent unauthorized use or access to it. The Account Holder agrees that no findings, files, or information derived from the use of the PSOPPC website may be released to unauthorized individuals. </p>
                <br />
                <p>The Account Holder agrees that in the event PSOPPC Security determines or has reasonable belief that the user has made any unauthorized disclosure(s), their access will be revoked. The Account Holder acknowledges that criminal, administrative, or civil penalties under the Privacy Act may apply if it is determined that the individual knowingly and willfully released the information under false pretenses.</p>
                <br />
                <p>Account Holders with access to the PSOPPC Help Center within the PSOPPC secure website are able to post and share information with other account holders. Account Holders warrant to the accuracy and completeness of the electronic documents and resources they provide. Account Holders agree not to market nor promote their services. Content supporting prohibited partisan political activity is not permitted. AHRQ makes no warranties, expressed or implied, regarding errors or omissions and assumes no legal liability or responsibility for loss or damage resulting from the use of information contained within. PSOPPC Help Center functionality provided by the PSOPPC does not constitute legal advice and does not constitute or imply approval of any submitted documents, official guidance, or a final decision by AHRQ. AHRQ retains the right to remove content from the PSOPPC Help Center.</p>
                <br />
                <p>
                  By accepting this agreement, the Account Holder agrees to abide by all provisions set forth in this Agreement for protection of the data and acknowledges having received notice of potential criminal, administrative, or civil penalties for violation of the terms of the Agreement.
                  In addition, by using the PSOPPC website, users agree with the terms of the User Agreement as defined on the PSOPPC website Registration Form.
                </p>
                <br />
                <TextButton
                  className="me-3"
                  variant="normal"
                  onClick={agreeTermOfUse}
                  content={"I Agree"}
                >
                </TextButton>
                <TextButton
                  variant="outline"
                  onClick={handleCancel}
                  content={"I Disagree"}
                >
                </TextButton>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    }
    </>
  )
}

export default TermOfUse;